import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, CreditCard, Bell, Star, Shield, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Vision = () => {
  const futureFeatures = [
    {
      icon: TrendingUp,
      title: 'Dashboard proprietario con stato pagamenti',
      description: 'Monitora in tempo reale tutti i tuoi affitti, scadenze e incassi'
    },
    {
      icon: CreditCard,
      title: 'Pagamenti integrati',
      description: 'Sistema di pagamento automatico sicuro e tracciabile'
    },
    {
      icon: Bell,
      title: 'Sistema di sollecito automatico',
      description: 'Notifiche automatiche per pagamenti in ritardo'
    },
    {
      icon: Star,
      title: 'Rating inquilini',
      description: 'Sistema di valutazione condiviso per una community più sicura'
    },
    {
      icon: Shield,
      title: 'Tutela legale automatizzata',
      description: 'Protezione legale integrata e assistenza in caso di controversie'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header with back button */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Link to="/">
            <Button variant="ghost" className="group">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Torna alla Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl animate-blob"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">Il Futuro degli Affitti</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            La nostra <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">visione</span>
          </h1>
          
          <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Reflow nascerà come piattaforma completa per la gestione dell'affitto, 
            trasformando il modo in cui proprietari e inquilini interagiscono.
          </p>
        </div>
      </section>

      {/* Current Phase */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <Card className="p-8 md:p-12 bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Fase Attuale</h3>
                <p className="text-lg text-slate-700">
                  Attualmente in fase di <span className="font-semibold text-emerald-600">lancio locale a Pescara</span>, 
                  con focus sulla selezione dei primi 50 immobili fondatori e sulla costruzione 
                  di una community di proprietari che credono nella nostra visione.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Future Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              La Piattaforma Completa
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Ecco cosa stiamo costruendo per rivoluzionare il mercato degli affitti
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {futureFeatures.map((feature, index) => (
              <Card 
                key={index} 
                className="p-8 bg-white border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Vision Statement */}
          <Card className="p-8 md:p-12 bg-gradient-to-br from-slate-900 to-blue-900 border-none text-center">
            <img 
              src="https://images.pexels.com/photos/9577224/pexels-photo-9577224.jpeg"
              alt="Technology"
              className="w-full h-64 object-cover rounded-xl mb-8 opacity-80"
            />
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Un ecosistema completo
            </h3>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Non solo un portale di annunci, ma un vero e proprio <span className="text-emerald-400 font-semibold">sistema operativo</span> che 
              gestisce ogni aspetto dell'affitto: dalla selezione dell'inquilino ai pagamenti, 
              dalla comunicazione alla tutela legale.
            </p>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Unisciti alla rivoluzione
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            I proprietari fondatori avranno accesso prioritario a tutte le funzionalità future
          </p>
          <Link to="/">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-10 py-7 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Diventa Proprietario Fondatore
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Vision;
